import type { Betriebsstoerung } from './api'

/**
 * **Die eine Stelle, an der Störungen ankommen** (7.2 Abschnitt 30, R1003,
 * R1006) — und an der steht, worauf die Anwendung gerade wartet (R1005).
 *
 * Ohne Bildschirmteil, damit die Schnittstellenschicht hierher melden kann,
 * ohne das Fenster zu kennen. Das Fenster liest von hier.
 *
 * **Gleiche Störungen stehen einmal da, mit ihrer Zahl** (R1006): Ist der
 * Dienst fort, schlagen zehn Abrufe der Startseite zugleich fehl — und zehnmal
 * „Verstanden" zu drücken, bevor man arbeiten kann, wäre die Strafe für eine
 * Lage, die der Anwender nicht verschuldet hat.
 */

export type Stoerungseintrag = {
  stoerung: Betriebsstoerung
  anzahl: number
}

let eintraege: Stoerungseintrag[] = []
const hoerer = new Set<() => void>()

function aendern(neu: Stoerungseintrag[]) {
  eintraege = neu
  for (const h of hoerer) {
    h()
  }
}

/** Woran zwei Störungen als dieselbe erkannt werden: an dem, was sie sagen. */
function art(s: Betriebsstoerung): string {
  return JSON.stringify([s.schluessel, s.meldung, s.handreichung])
}

export function meldeStoerung(stoerung: Betriebsstoerung): void {
  const da = eintraege.find((e) => art(e.stoerung) === art(stoerung))
  aendern(
    da
      ? eintraege.map((e) => (e === da ? { ...e, anzahl: e.anzahl + 1 } : e))
      : [...eintraege, { stoerung, anzahl: 1 }],
  )
}

export function quittiereStoerungen(): void {
  aendern([])
}

export function stoerungen(): Stoerungseintrag[] {
  return eintraege
}

export function abonniereStoerungen(h: () => void): () => void {
  hoerer.add(h)
  return () => {
    hoerer.delete(h)
  }
}

/* --- Worauf die Anwendung wartet (R1005) ------------------------------------ */

let naechste = 0
const laufend = new Map<number, { beginn: number; knopf: HTMLButtonElement | null }>()

/* --- Der Knopf, der die Arbeit angestoßen hat (7.2 Abschnitt 40, R1250) ----- */

/** Ab wann der Kreis am Knopf erscheint — ein schneller Abruf soll nicht flackern. */
export const KREIS_AB_MS = 150

let geklickt: HTMLButtonElement | null = null
const arbeitende = new Map<HTMLButtonElement, number>()

/**
 * **Wer einen Knopf drückt, sieht an ihm, dass gearbeitet wird** (R1250).
 * Gemerkt wird der Knopf nur für die Dauer des Klicks selbst: Was dessen
 * Behandlung an Abrufen beginnt, gehört zu ihm; was später im Hintergrund
 * kommt, nicht.
 *
 * Einmal eingehängt, vor jeder Seite — im Fang, damit der Knopf schon
 * feststeht, wenn die Seite ihren Klick bekommt.
 */
export function beobachteKnoepfe(wurzel: Document = document): () => void {
  const merke = (ereignis: Event) => {
    const ziel = ereignis.target instanceof Element ? ereignis.target.closest('button') : null
    if (!ziel) {
      return
    }
    geklickt = ziel
    window.setTimeout(() => {
      if (geklickt === ziel) {
        geklickt = null
      }
    }, 0)
  }
  wurzel.addEventListener('click', merke, true)
  return () => wurzel.removeEventListener('click', merke, true)
}

function knopfArbeitet(knopf: HTMLButtonElement): () => void {
  let gesetzt = false
  const uhr = window.setTimeout(() => {
    gesetzt = true
    const zahl = (arbeitende.get(knopf) ?? 0) + 1
    arbeitende.set(knopf, zahl)
    knopf.setAttribute('aria-busy', 'true')
  }, KREIS_AB_MS)

  return () => {
    window.clearTimeout(uhr)
    if (!gesetzt) {
      return
    }
    const zahl = (arbeitende.get(knopf) ?? 1) - 1
    if (zahl > 0) {
      arbeitende.set(knopf, zahl)
      return
    }
    arbeitende.delete(knopf)
    knopf.removeAttribute('aria-busy')
  }
}

/** Ein Abruf beginnt; der Rückgabewert meldet ihn fertig. */
export function beginneAbruf(): () => void {
  const nummer = ++naechste
  const knopf = geklickt
  laufend.set(nummer, { beginn: Date.now(), knopf })
  const knopfFertig = knopf ? knopfArbeitet(knopf) : null
  return () => {
    laufend.delete(nummer)
    knopfFertig?.()
  }
}

/**
 * **Die gemeinsame Abrufstelle** (R1250, R1255): Jeder Ruf der Oberfläche an
 * den Dienst geht hier durch und nirgends daran vorbei. Nur so weiß die
 * Anwendung, worauf sie wartet — und welcher Knopf es angestoßen hat.
 */
export async function rufe(pfad: string, einstellungen?: RequestInit): Promise<Response> {
  const fertig = beginneAbruf()
  try {
    return await fetch(pfad, einstellungen)
  } finally {
    fertig()
  }
}

let eigene = 0

/**
 * Eine Seite zeigt ihr Warten selbst — der EZB-Abruf mit seinem Balken
 * (R1008). Solange tritt der allgemeine Hinweis zurück. Der Rückgabewert gibt
 * die Stelle wieder frei.
 */
export function zeigtSelbst(): () => void {
  eigene++
  return () => {
    eigene--
  }
}

/**
 * Seit wann der älteste laufende Abruf wartet — `null`, wenn keiner läuft
 * oder eine Seite ihr Warten selbst zeigt.
 */
export function wartetSeit(): number | null {
  if (eigene > 0) {
    return null
  }

  let aeltester: number | null = null
  for (const { beginn } of laufend.values()) {
    if (aeltester === null || beginn < aeltester) {
      aeltester = beginn
    }
  }
  return aeltester
}

/**
 * Seit wann ein Abruf läuft, dessen Knopf mit dem Klick verschwunden ist —
 * eine Rückfrage, die sich mit *Ja* geschlossen hat (R1251). Dann gibt es
 * keinen Ort für den Kreis, und der Hinweis am unteren Rand kommt sofort.
 */
export function knopflosSeit(): number | null {
  if (eigene > 0) {
    return null
  }

  let aeltester: number | null = null
  for (const { beginn, knopf } of laufend.values()) {
    if (knopf && !knopf.isConnected && (aeltester === null || beginn < aeltester)) {
      aeltester = beginn
    }
  }
  return aeltester
}
